import type React from 'react';
import { useState, useCallback, useEffect } from 'react';

import { FlexColumn } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Button } from 'ui-components/components/button';
import { RadioCheckList } from 'ui-components/components/radio-check-list';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';

import { RateShopperOption } from './rate-shopper-option';
import { useRateShopperContext } from '../../../../../settings/rate-shopper/context/rate-shopper-context';
import { useRateShopperAutomationRule } from '../../../../../../contexts/rate-shopper-automation-rule-context';
import { Segment } from '../../../../../../services/analytics-services/segment';
import { RATE_SHOPPER_SELECTION_CHANGE } from '../../../../../../constants/analytics-events';
import { THEME_CONSTANTS } from '../../../../../../ui-components/services/button-theme-service';
import { getAutomationsSettingsPath } from '../../../../../../routes/constants';
import messages from './messages';
import style from './style.module.less';

const CHEAPEST_GUID = 'ada7df25-768f-4400-8c71-410c1c957941';
const FASTEST_GUID = 'c2d3e4f5-6a7b-8c9d-0e1f-2a3b4c5d6e7f';
const BEST_VALUE_GUID = 'b1c8f0d2-3e4f-4c5a-9b6d-7e8f9a0b1c2d';

const CHEAPEST = 'cheapest';
const FASTEST = 'fastest';
const BEST_VALUE = 'best_value';
const FTU_WIZARD_DISCOUNTED_RATES_STEP = 'ftu_wizard_discounted_rates_step';

export const RateShopperSelectionList = () => {
  const { isRequestingRateShoppers } = useRateShopperContext();
  const { currentSelection, isLoading: isLoadingAutomation, updateRateShopperSelection, error } = useRateShopperAutomationRule();

  const [selectedOption, setSelectedOption] = useState(currentSelection ?? BEST_VALUE_GUID);

  useEffect(() => {
    setSelectedOption(currentSelection ?? BEST_VALUE_GUID);
  }, [currentSelection]);

  const handleOptionSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const newSelection = e.target.value;
      setSelectedOption(newSelection);

      let selectionType: string;
      if (newSelection === BEST_VALUE_GUID) {
        selectionType = BEST_VALUE;
      } else if (newSelection === FASTEST_GUID) {
        selectionType = FASTEST;
      } else {
        selectionType = CHEAPEST;
      }

      try {
        Segment.trackEvent(RATE_SHOPPER_SELECTION_CHANGE, {
          selection: selectionType,
          selectionGuid: newSelection,
          source: FTU_WIZARD_DISCOUNTED_RATES_STEP,
        });
        await updateRateShopperSelection(newSelection);
      } catch {
        setSelectedOption(currentSelection ?? BEST_VALUE_GUID);
      }
    },
    [currentSelection, updateRateShopperSelection],
  );

  if (isRequestingRateShoppers || isLoadingAutomation) {
    return (
      <FlexColumn className={style.section}>
        <Text textType={TextType.H3} value={messages.carrierChoiceTitle} />
        <Text textType={TextType.Paragraph} value={messages.carrierChoiceDescription} />
        <LoadingIndicator className={style.loading} />
      </FlexColumn>
    );
  }

  return (
    <FlexColumn className={style.section}>
      {error && (
        <BannerMessageV2
          content={
            <Text
              formattedMessageValues={{
                a: (chunks) => (
                  <Button href={getAutomationsSettingsPath()} target='_self' theme={THEME_CONSTANTS.link}>
                    {chunks}
                  </Button>
                ),
              }}
              textType={TextType.Paragraph}
              value={error}
            />
          }
          layout={BannerLayout.Tiny}
          status={BannerStatus.Error}
        />
      )}
      <Text textType={TextType.H3} value={messages.carrierChoiceTitle} />
      <Text textType={TextType.Paragraph} value={messages.carrierChoiceDescription} />
      <RadioCheckList className={style['rate-shopper-selection-list']}>
        <RateShopperOption
          defaultBadgeText={messages.defaultBadge}
          description={messages.recommendedOptionDescription}
          id={BEST_VALUE_GUID}
          isSelected={selectedOption === BEST_VALUE_GUID}
          onSelect={handleOptionSelect}
          showDefaultBadge
          title={messages.bestValueOptionTitle}
          tooltipText={messages.bestTooltip}
          value={BEST_VALUE_GUID}
        />
        <RateShopperOption
          description={messages.cheapestOptionDescription}
          id={CHEAPEST_GUID}
          isSelected={selectedOption === CHEAPEST_GUID}
          onSelect={handleOptionSelect}
          title={messages.cheapestOptionTitle}
          tooltipText={messages.cheapestTooltip}
          value={CHEAPEST_GUID}
        />
        <RateShopperOption
          description={messages.fastestOptionDescription}
          id={FASTEST_GUID}
          isSelected={selectedOption === FASTEST_GUID}
          onSelect={handleOptionSelect}
          title={messages.fastestOptionTitle}
          tooltipText={messages.fastestTooltip}
          value={FASTEST_GUID}
        />
      </RadioCheckList>
    </FlexColumn>
  );
};
